import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';
import { fetchJSON, putJSON } from 'api/fetch';
import { RootState } from 'initializers/types';
import { createSelector } from 'reselect';

import { PARENT_SLICE_NAME, SLICE_NAME } from './constants';
import {
  EstimatedSale,
  EstimatedSalesParams,
  EstimatedSalesPayloadItem,
  EstimatedSalesRequestPayload,
  EstimatedSalesResponsePayload,
  EstimatedSalesState,
  EstimatesSalesUpdateResponsePayload,
} from './types';

export const fetchEstimatedSales = createAsyncThunk<
  EstimatedSalesResponsePayload,
  EstimatedSalesParams
>(`${SLICE_NAME}/fetch`, async (params: EstimatedSalesParams) => {
  const resp = await fetchJSON(
    `/schedule_builder/estimated_sales?end_date=${params.endDate}&start_date=${params.startDate}`
  );
  return resp;
});

export const updateEstimatedSales = createAsyncThunk<
  EstimatesSalesUpdateResponsePayload,
  EstimatedSalesRequestPayload
>(
  `${SLICE_NAME}/update`,
  async ({ id, payload }: EstimatedSalesRequestPayload) => {
    const resp = await putJSON(`/schedule_builder/estimated_sales/${id}`, {
      data: { attributes: payload },
    });
    return resp;
  }
);

const estimatedSalesAdapter = createEntityAdapter<EstimatedSale>();

export const initialState: EstimatedSalesState =
  estimatedSalesAdapter.getInitialState({
    status: 'idle',
    error: undefined,
  });

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    removeAll: estimatedSalesAdapter.removeAll,
  },
  extraReducers: builder => {
    builder.addCase(fetchEstimatedSales.pending, state => {
      state.status = 'pending';
    });
    builder.addCase(fetchEstimatedSales.fulfilled, (state, action) => {
      const items = action.payload.data.map(
        (item: EstimatedSalesPayloadItem) => ({
          id: item.id,
          type: item.type,
          ...item.attributes,
        })
      );

      estimatedSalesAdapter.setAll(state, items);
      state.status = 'succeeded';
    });
    builder.addCase(fetchEstimatedSales.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to fetch estimated sales';
    });
    builder.addCase(updateEstimatedSales.pending, state => {
      state.status = 'pending';
    });
    builder.addCase(updateEstimatedSales.fulfilled, (state, action) => {
      const item: EstimatedSalesPayloadItem = action.payload.data;

      estimatedSalesAdapter.updateOne(state, {
        id: item.id,
        changes: {
          manual: item.attributes.manual,
          value: item.attributes.value,
        },
      });
      state.status = 'succeeded';
    });
  },
});

const { selectAll, selectById } = estimatedSalesAdapter.getSelectors(
  (state: RootState) => state.get?.(PARENT_SLICE_NAME)?.[SLICE_NAME]
);

export const selectEstimatedSalesFetchIsPending = (state: RootState) =>
  state.get?.(PARENT_SLICE_NAME)?.[SLICE_NAME]?.status === 'pending';

export const selectEstimatedSalesValueByDate = (
  state: RootState,
  date: string
) => selectById(state, date)?.value;

export const selectEstimatedSalesManualByDate = (
  state: RootState,
  date: string
) => selectById(state, date)?.manual;

export const selectSummarySales = createSelector(selectAll, sales =>
  sales.reduce((memo, sale) => memo + sale.value, 0)
);

export const { reducer, actions } = slice;

export const {
  selectById: selectEstimatedSalesById,
  selectIds: selectEstimatedSalesIds,
  selectEntities: selectEstimatedSalesEntities,
  selectAll: selectAllEstimatedSales,
  selectTotal: selectTotalEstimatedSales,
} = estimatedSalesAdapter.getSelectors();
